/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, inject, signal} from '@angular/core';
import * as _ from 'lodash';
import template from './folder-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {TeamApiService} from '../admin/team/team-api.service';
import {deferSignalViewWrite} from '../common/util/template-state';
import {FolderApiService, FolderModel} from './folder-api.service';
import {FormsModule} from '@angular/forms';
import {AppSharedModule} from '../common/app-shared.module';

interface FolderExtDatum {
  key: string;
  value: string;
}

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule],
  selector: 'adn-folder-edit-page',
  template
})
export class FolderEditPageComponent extends SignalAdminResourceEditPageBase<FolderModel> {
  extData: FolderExtDatum[] = [];
  allTeams: any[] | null = null;
  teamsWarning = false;
  readonly canUpdateFolder = true;
  readonly viewRevision = signal(0);

  constructor(
    private readonly teamApi: TeamApiService
  ) {
    super(inject(FolderApiService), 'folder');

    this.teamApi.listOptions().subscribe((page) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalViewWrite(this.viewRevision, () => {
        this.allTeams = page.results;
      });
    });
  }

  protected override initialiseModel(model: FolderModel): FolderModel {
    const initialised = super.initialiseModel(model);
    initialised.dataSource = initialised.dataSource || 'ADNUNTIUS';
    this.extData = _.map(initialised.data || {}, (value: string, key: string) => ({key, value}));
    initialised.teams = this.normalizeTeamIds(initialised.teams);
    return initialised;
  }

  protected override afterSave(model: FolderModel): void {
    const savedTeams = _.get(model, 'teams') || [];
    this.teamsWarning = (this.editingModel.teams || []).length !== savedTeams.length;
    model.teams = this.normalizeTeamIds(savedTeams);
  }

  protected override prepareForSubmit(model: FolderModel): void {
    model.data = {};
    _.forEach(_.filter(this.extData, (datum) => {
      return _.trim(datum.value).length > 0 && _.trim(datum.key).length > 0;
    }), (datum) => {
      model.data[datum.key] = datum.value;
    });
  }

  addData(): void {
    this.extData = [...this.extData, {key: '', value: ''}];
    this.markExternalFormDirty();
  }

  deleteData(index: number): void {
    this.extData = this.extData.filter((_datum, i) => i !== index);
    this.markExternalFormDirty();
  }

  onExtDataChange(): void {
    this.markExternalFormDirty();
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.allTeams, 'id');
    this.markExternalFormDirty();
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.markExternalFormDirty();
  }

  isTeamSelected(teamId: string): boolean {
    return (this.editingModel.teams || []).indexOf(teamId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const teams = [...(this.editingModel.teams || [])];
    const index = teams.indexOf(teamId);

    if (checked && index < 0) {
      teams.push(teamId);
    } else if (!checked && index > -1) {
      teams.splice(index, 1);
    }

    this.editingModel.teams = teams;
    this.markExternalFormDirty();
  }

  trackByTeamId(_index: number, team: any): string {
    return team.id;
  }

  trackByExtDataIndex(index: number): number {
    return index;
  }

  private normalizeTeamIds(teams: any): string[] {
    return _.map(teams || [], (team: any) => _.isString(team) ? team : team.id);
  }
}
